import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/auth';
import { TIER_FEATURES, useGetSubscriptionPlans } from '@/entities/subscription';
import type { PlanInterval } from '@/entities/subscription';
import { toError } from '@/shared/lib/toError';
import { PlanHeroCard } from '../plan-status';
import { BillingHistoryTable } from '../billing-history';
import { UpgradePlanCard } from '../upgrade-plan';
import { CancelSubscriptionRow } from '../cancel-subscription';
import { useUpgradeSubscription } from '../upgrade-in-place';
import { useOpenPortal } from '../manage-subscription';
import { findPlan } from '../model/findPlan';
import { getUpgradeFeatures } from '../model/getUpgradeFeatures';
import { BillingIntervalToggle, type BillingInterval } from './BillingIntervalToggle';

type Props = {
  onPortalError: (error: Error) => void;
  onCheckoutError: (error: Error) => void;
};

const PRO_FEATURE_KEYS = TIER_FEATURES.pro.map((feature) => `subscription.features.${feature}`);

const toBillingInterval = (interval: PlanInterval | null): BillingInterval => {
  return interval === 'month' ? 'month' : 'year';
};

export const ProTierLayout = ({ onPortalError, onCheckoutError }: Props) => {
  const { t } = useTranslation();
  const { sessionUser } = useAuth();
  const currentInterval = sessionUser?.subscription.interval ?? null;
  const [upgradeInterval, setUpgradeInterval] = useState<BillingInterval>(toBillingInterval(currentInterval));

  const { data: plansData } = useGetSubscriptionPlans();
  const upgradeMutation = useUpgradeSubscription();
  const portalMutation = useOpenPortal();

  const plans = plansData?.plans ?? [];
  const proPlan = findPlan(plans, 'pro');
  const elitePlan = findPlan(plans, 'elite');

  const handleManageBilling = () => {
    portalMutation.mutate(
      { data: {} },
      {
        onSuccess: (data) => {
          window.open(data.portalUrl, '_blank');
        },
        onError: (error) => {
          onPortalError(toError(error));
        },
      }
    );
  };

  const handleEliteUpgrade = () => {
    upgradeMutation.mutate(
      { data: { targetTier: 'elite', interval: upgradeInterval } },
      {
        onSuccess: (data) => {
          // No active subscription to swap in place → the API returns a Checkout
          // URL to finish the upgrade on Stripe. An in-place swap returns no URL;
          // the wrapper hook's cache invalidation re-renders this page as Elite.
          if (data.checkoutUrl) {
            window.location.href = data.checkoutUrl;
          }
        },
        onError: (error) => {
          onCheckoutError(toError(error));
        },
      }
    );
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 1000 }}>
      <PlanHeroCard
        tier="pro"
        interval={currentInterval}
        nextBillingDate={sessionUser?.subscription.expiresAt ?? null}
        features={PRO_FEATURE_KEYS}
        priceMonthly={proPlan?.monthly?.amount}
        onManageBilling={handleManageBilling}
        manageBillingLoading={portalMutation.isPending}
      />

      <BillingHistoryTable emptyState="paid" />

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h6">{t('subscription.upgrade.titleFromPro')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('subscription.upgrade.subtitleFromPro')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <BillingIntervalToggle value={upgradeInterval} onChange={setUpgradeInterval} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ maxWidth: 520, width: '100%' }}>
            {elitePlan && (
              <UpgradePlanCard
                plan={elitePlan}
                interval={upgradeInterval}
                previousTierLabel="Pro"
                audienceTagline={t('subscription.upgrade.forMultiLocation')}
                features={getUpgradeFeatures('elite')}
                variant="from-pro"
                ctaLabel="subscription.upgrade.upgradeToElite"
                onUpgrade={handleEliteUpgrade}
                upgradeLoading={upgradeMutation.isPending}
              />
            )}
          </Box>
        </Box>
      </Box>

      <CancelSubscriptionRow onMutationError={onPortalError} />
    </Stack>
  );
};
